import React from 'react';
import { Dropdown as BootstrapDropdown } from 'react-bootstrap';
import { Button as ButtonStyle, Subhead1 } from '../../../theme/styles';
import { translate } from '../../../translations';
import Checkbox, { CheckboxLozenge } from '../../Checkbox/Checkbox';
import Form from '../../Form/Form';
import Search from '../../Search/Search';
import Button from '../../Button/Button';
import '../Dropdown.css';

const SearchHeader = React.forwardRef((props, ref) => (
    <div ref={ref} className="dropdownListSearchContainer">
        <Search
            includeBorder
            placeholder={props.search.placeholder}
            placeholderStyle={props.search.placeholderStyle}
            value={props.search.value}
            onChange={props.search.onSearchChange}
            onClearInput={props.search.onSearchClear}
            isLoading={props.search.isLoading}
        />
    </div>
));

/**
 * @component DropdownList - rendered by the Dropdown component when passed a prop of dropdownType="dropdownList", displays selectable options with checkbox
 * @param {array} options - array of options to be displayed in dropdown
 * @example options props used in <DropdownList />, received from <Dropdown />
 * const options = [
 *    {
 *      label: "true",
 *      hide: "false",
 *      labelKey: "US",
 *      text: "United States",
 *    },
 *    {
 *      key: "AR",
 *      text: "Arkansas",
 *      value: "AR",
 *      checked: true,
 *      indeterminate: false,
 *      count: "1",
 *      hideCount: "true",
 *      hideCheckbox: "true",
 *    },
 */

export class DropdownList extends React.PureComponent {
    renderCheckboxContent = (option, index) => {
        if (!!option.hideCheckbox) {
            return false;
        }

        if (option.label) {
            return (
                <div key={index} className={`dropdownLabel ${option.hide ? 'hide' : 'show'}`} style={Subhead1}>
                    {option.text}
                </div>
            );
        }

        return (
            <Checkbox
                className="dropdownCheckboxContainer"
                key={index}
                option={option}
                cellClick={this.props.onDropdownCellClick}
            />
        );
    };

    getOptionClassNames = ({ selected, checked, isParent }) => (
        [
            selected || checked ? 'drop-down-list-selected' : '',
            isParent ? 'dropdownListParentOption' : '',
        ].join(' ').trim()
    );

    renderContent = (options, selectionType) => {
        const { labels, hideRadioInput } = this.props;
        switch (selectionType) {
            case 'checkbox':
                return options?.map((option, index) => this.renderCheckboxContent(option, index));
            case 'radio':
                return (
                    <Form
                        groupClass={['dropdownRadioGroup', hideRadioInput && 'hideRadioInput'].filter(Boolean).join(' ')}
                        groupName="dropdownRadioGroup"
                        inputType="radio"
                        data={options}
                        key={`form-${this.props.uniqueKey}`}
                        uniqueKey={this.props.uniqueKey}
                        clickFunction={(option) => {
                            this.props.onDropdownCellClick(option);

                            if (this.props.closeOnSelectRadio) {
                                this.props.dropdownCloseFunction();
                            }
                        }}
                        labels={labels}
                        hideRadioInput={hideRadioInput}
                    />
                );
            default:
                return options?.map((option, index) => (
                    <BootstrapDropdown.Item
                        key={option.key || index}
                        className={this.getOptionClassNames(option)}
                        text={option.text}
                        onClick={this.props.onDropdownCellClick ? () => this.props.onDropdownCellClick(option) : null}
                        style={ButtonStyle}
                        disabled={option?.disabled}
                    >
                        {option.text && !option.subText ? option.text : null}
                        {option.text && option.subText
                            ? (
                                <div className={['dropdownOptionLabel', option?.rightLozenges?.length ? 'spaceBetween' : ''].join(' ').trim()}>
                                    {option.text}
                                    {option.subText ? <span className="dropdownOptionSubText">{option.subText}</span> : null}
                                    {option?.rightLozenges?.map((loz) => <CheckboxLozenge lozengeText={loz.lozengeText} className={loz.className} />) || null}
                                </div>
                            ) : null}
                    </BootstrapDropdown.Item>
                ));
        }
    };

    render() {
        const {
            search,
            options,
            headerCheckboxOptions,
            selectionType,
            recentlyUsed,
            recentlyUsedTitle,
        } = this.props;

        const sadSearchSvg = '/main/images/noContent/blankSearch.svg';

        return (
            <BootstrapDropdown.Menu className={['dropdownListMenu', selectionType === 'radio' ? 'radioListMenu' : ''].join(' ')} align="left">
                {search ? (
                    <>
                        <BootstrapDropdown.Header as={SearchHeader} search={search} />
                        <BootstrapDropdown.Divider />
                        {search?.searchRequired && search?.showSelectControls ? (
                            <>
                                {selectionType ? (
                                    <div className="requiredSearchActions">
                                        <Button
                                            variant="link"
                                            content={translate('select_all')}
                                            onClick={search?.handleSelectAll}
                                            disabled={search?.disableSelectAllBeforeFilter && options?.length === 0}
                                        />
                                        <Button
                                            variant="link"
                                            content={translate('clear')}
                                            onClick={search?.handleClear}
                                        />
                                    </div>
                                ) : (
                                    <div className="singleSelectClearSelection">
                                        <Button
                                            variant="link"
                                            content={translate('clear_selection')}
                                            onClick={search?.handleClear}
                                        />
                                    </div>
                                )}

                            </>
                        ) : null}
                    </>
                ) : null}
                <div className="dropdownListMenuContent overlay-scrollbar-thin">
                    {search && search?.value?.length > 0 && (
                        <div className="resultsHeader">
                            <h2 style={Subhead1}>{translate('search_results')}</h2>
                        </div>
                    )}
                    {(options?.length > 0) ? (
                        <>
                            {headerCheckboxOptions && (
                                <Checkbox
                                    className="dropdownCheckboxContainer"
                                    key={headerCheckboxOptions.key}
                                    option={headerCheckboxOptions}
                                    cellClick={headerCheckboxOptions.onClick}
                                />
                            )}
                            {this.renderContent(options, selectionType)}
                        </>
                    ) : null}
                    {options?.length === 0 && search?.searchRequired && ((recentlyUsed?.length === 0)
                        || (recentlyUsed?.length > 0 && search?.value.length > 0)) ? (
                            <div className="dropdownNoResults">
                                <img src={sadSearchSvg} alt="empty box" className="dropdownNoResultsImage" />
                                {search.isLoading ? translate('loading') : (
                                    search.searchRequiredMessage ?? translate('start_typing_to_see_results'))}
                            </div>
                        ) : null}
                    {recentlyUsed && recentlyUsed.length > 0 && options.length === 0 && search?.value.length === 0 && (
                        <>
                            {recentlyUsed.length > 0 && (
                                <div className="recentlyUsed">
                                    <div className="resultsHeader">
                                        <h2 style={Subhead1}>{recentlyUsedTitle}</h2>
                                    </div>
                                    {this.renderContent(recentlyUsed, selectionType)}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </BootstrapDropdown.Menu>
        );
    }
}

export default DropdownList;
